import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Logo from "./Logo";
import { Box, chakra } from "@chakra-ui/react";
import { COLORS } from "../../globalvariables";

const MotionHeading = chakra(motion.h2);

function HeroSection({ title, subtitle, buttonText, phrases, activeCofounderModal }) {
  const [showTypingText, setShowTypingText] = useState(false);
  const [typingEffect, setTypingEffect] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [charIndex, setCharIndex] = useState(0);

  const phrasesToUse = phrases;

  useEffect(() => {
    const shrinkTimer = setTimeout(() => {
      setTimeout(() => {
        setShowTypingText(true);
        setIsTyping(true);
      }, 500);
    }, 1000);

    return () => clearTimeout(shrinkTimer);
  }, []);

  useEffect(() => {
    if (isTyping) {
      const typingInterval = setInterval(() => {
        if (charIndex < phrasesToUse[0].length) {
          setTypingEffect((prev) => prev + phrasesToUse[0][charIndex]);
          setCharIndex((prev) => prev + 1);
        } else {
          clearInterval(typingInterval);
          setTimeout(() => {
            setTypingEffect("");
            setCharIndex(0);
            setIsTyping(true);
          }, 2000);
        }
      }, 100);
      return () => clearInterval(typingInterval);
    }
  }, [charIndex, isTyping, phrasesToUse]);

  return (
    <Box
      as="section"
      id="hero"
      display={activeCofounderModal ? "none" : "flex"}
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      color={COLORS.textPrimary}
      position="relative"
      zIndex={-1}
      minH={{ base: "70vh", md: "80vh" }}
      px={{ base: 2, md: 4 }}
    >
      {/* Glow effect */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        width={{ base: "300px", md: "600px" }}
        height={{ base: "300px", md: "600px" }}
        borderRadius="full"
        bg={`radial-gradient(circle, ${COLORS.primary}33 0%, transparent 70%)`}
        filter="blur(60px)"
        zIndex={0}
        opacity={0.6}
        animation="pulse 6s infinite ease-in-out"
      />

      {/* Logo */}
      <Box 
        position="relative" 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        pt={{ base: 6, md: -40 }}
        zIndex={2}
      >
        <Logo />
      </Box>

      {/* Typing Text */}
      <Box
        textAlign="center"
        maxW={{ base: "85%", md: "3xl" }}
        mx="auto"
        mt={{ base: 4, md: -28 }}
        px={{ base: 2, md: 4 }}
        position="relative"
        zIndex={-1}
        minH={{ base: "100px", md: "120px" }}
        maxH={{ base: "100px", md: "120px" }}
        overflow="hidden"
      >
        <MotionHeading
          fontSize={{ base: "xl", sm: "2xl", md: "4xl" }}
          fontWeight="bold"
          mx="auto"
          visibility={showTypingText ? "visible" : "hidden"}
          className="typing-effect"
          bgGradient={`linear(to-r, ${COLORS.textPrimary}, ${COLORS.primary} 30%, ${COLORS.textPrimary})`}
          bgClip="text"
          animation={showTypingText ? "colorPulse 8s infinite alternate" : "none"}
          letterSpacing={{ base: "0.05em", md: "0.1em" }}
          lineHeight={{ base: "1.3", md: "1.5" }}
        >
          {typingEffect || (showTypingText ? "" : phrasesToUse[0])}
        </MotionHeading>
      </Box>

      {/* Styles */}
      <style jsx global>{`
        .typing-effect {
          font-family: "Helvetica", sans-serif;
          font-weight: bold;
          white-space: pre-wrap;
          margin: 0 auto;
          letter-spacing: 0.1em;
          max-width: 100%;
        }

        @media (max-width: 768px) {
          .typing-effect {
            max-width: 90%;
            font-size: 1.25rem !important;
            line-height: 1.3 !important;
            letter-spacing: 0.05em !important;
          }
        }

        @keyframes typing {
          from { width: 0; }
          to { width: 100%; }
        }

        @keyframes blink-caret {
          from, to { border-color: transparent; }
          50% { border-color: ${COLORS.textPrimary}; }
        }

        @keyframes colorPulse {
          0%   { filter: hue-rotate(0deg) brightness(1); }
          50%  { filter: hue-rotate(30deg) brightness(1.1); }
          100% { filter: hue-rotate(0deg) brightness(1); }
        }

        @keyframes pulse {
          0%   { opacity: 0.4; transform: translate(-50%, -50%) scale(0.95); }
          50%  { opacity: 0.6; transform: translate(-50%, -50%) scale(1.05); }
          100% { opacity: 0.4; transform: translate(-50%, -50%) scale(0.95); }
        }
      `}</style>
    </Box>
  );
}

export default HeroSection;